// Referral attribution on the client.
//
// A referral can arrive two ways: as a link (`/register?ref=CODE`, which may be
// the very first URL a visitor ever opens) or typed into the signup form. Both
// end up in the same place — the `referral_code` field on the register call.
//
// The link case needs somewhere to survive the trip from landing page to signup
// (and, for Google sign-in, the whole OAuth round-trip through accounts.google
// .com and back). sessionStorage is that place: it lasts the browsing session,
// dies with the tab, and never leaks into another origin.

const STORAGE_KEY = 'storyfy_referral_code'

/** Codes are uppercase and drawn from an unambiguous alphabet (see the backend's
 *  REFERRAL_ALPHABET). Normalising here means "  abc-123 " pasted from a chat
 *  message still matches, and a junk value never reaches the API. */
export const normalizeReferralCode = (raw?: string | null): string =>
  (raw || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 24)

/** Remember a `?ref=` code from the current URL, if there is one. Safe to call
 *  on every page load; an existing stored code is not overwritten by an empty
 *  one, so navigating away from the referral link doesn't drop the attribution. */
export const captureReferralFromUrl = (search: string = window.location.search): string => {
  const code = normalizeReferralCode(new URLSearchParams(search).get('ref'))
  if (code) {
    try {
      sessionStorage.setItem(STORAGE_KEY, code)
    } catch {
      // Private mode / storage disabled — the code simply isn't remembered.
    }
  }
  return code || getStoredReferralCode()
}

export const getStoredReferralCode = (): string => {
  try {
    return sessionStorage.getItem(STORAGE_KEY) || ''
  } catch {
    return ''
  }
}

/** Called once a signup has consumed the code, so a later account created in
 *  the same tab isn't attributed to the same referrer by accident. */
export const clearStoredReferralCode = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    /* nothing to clear */
  }
}
